import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Stage } from '@/domain';
import { useArchiveRepository, useJobOrderRepository, useKitRepository } from '../container';
import { keys } from '../query-keys';
import type { ArchivedCaseFilter } from '../ports/ArchiveRepository';
import type { BulkResult } from '../ports/JobOrderRepository';
import type { BulkAssignCommand } from '../ports/KitRepository';

/**
 * The commands on a selection of cases. Section 4 of the request of
 * 24 September 2026: "allow option to select multiple cases in orders, archive,
 * production line, and allow different actions for the selected cases like:
 * delete all, archive all, assign all to a particular department or person".
 *
 * Each answers one outcome per case, so the screen can say which went and why
 * the others did not. None is queued offline and none is optimistic: they are a
 * manager's decisions about many cases at once, and showing forty cases as
 * archived before the server agreed is how a list ends up disagreeing with
 * itself. Everything is refetched afterwards, as a deletion refetches
 * everything: an archived case leaves the orders, the benches, the board and
 * the queues at once, and joins the archive.
 */
function useBulkMutation<Input>(run: (input: Input) => Promise<BulkResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void queryClient.invalidateQueries();
    },
  });
}

/** Requires `archiveCase`. Off every list of work in hand, into the Archived tab. */
export function useArchiveCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((input: { jobOrderNos: string[]; reason?: string }) =>
    repo.archiveMany(input.jobOrderNos, input.reason),
  );
}

/** Requires `archiveCase`. Back exactly as they went in. */
export function useUnarchiveCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((input: { jobOrderNos: string[] }) => repo.unarchiveMany(input.jobOrderNos));
}

/** Requires `deleteCase`. Outright for a holder of `approveDeletion`, asked for otherwise. */
export function useDeleteCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((input: { jobOrderNos: string[]; reason: string }) =>
    repo.deleteMany(input.jobOrderNos, input.reason),
  );
}

/*
 * The money actions on many cases at once, and the Undo of each. The lab's
 * request of 17 October 2026, items 9 and 10. All require `pay`.
 */
export function usePayCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((jobOrderNos: string[]) => repo.payMany(jobOrderNos));
}

export function useUnpayCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((jobOrderNos: string[]) => repo.unpayMany(jobOrderNos));
}

export function useReleaseCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((jobOrderNos: string[]) => repo.releaseMany(jobOrderNos));
}

export function useUnreleaseCases() {
  const repo = useJobOrderRepository();
  return useBulkMutation((jobOrderNos: string[]) => repo.unreleaseMany(jobOrderNos));
}

/** Requires `assign`. To a department, a person, or both. */
export function useAssignCases() {
  const repo = useKitRepository();
  return useBulkMutation((command: BulkAssignCommand) => repo.assignMany(command));
}

/**
 * Whoever works a bench, for the picker on a selection. Under the kits prefix,
 * as a tray's own candidates are, so anything that moves a kit refreshes it.
 */
export function useStageAssignees(stage: Stage | null) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: [...keys.kits.all, 'stage-assignees', stage ?? ''],
    queryFn: () => repo.stageAssignees(stage as Stage),
    enabled: stage !== null,
    staleTime: 60_000,
  });
}

/**
 * The archive's Archived tab. Keyed like the other two sets, under the archive
 * prefix, so anything that invalidates the archive refetches it with them.
 */
export function useArchivedCases(filter: ArchivedCaseFilter) {
  const repo = useArchiveRepository();
  return useQuery({
    queryKey: keys.archive(filter),
    queryFn: () => repo.archived(filter),
    // Keeps the previous page on screen while a new filter loads, as the archive does.
    placeholderData: (previous) => previous,
  });
}
